import React from "react"
import PropTypes from "prop-types"
import { graphql } from "gatsby"
import Reveal from "../utils/Reveal"

import Card from "../atoms/Card"
import Section from "../atoms/Section"
import FlexGrid from "../atoms/FlexGrid"
import Button from "../atoms/Button"
import Flex from "../atoms/Flex"
import Box from "../atoms/Box"
import LayoutChanger from "../atoms/LayoutChanger"

import InfoCard from "../molecules/InfoCard"
import SectionTitle from "../molecules/SectionTitle"
import FilteredGrid from "../molecules/FilteredGrid"

const toFlexAlign = value =>
  value === "left" ? "start" : value === "right" ? "end" : value || "center"

const toJustifyContent = align =>
  align === "start" ? "flex-start" : align === "end" ? "flex-end" : "center"

const toAlignSelf = align =>
  align === "start" ? "flex-start" : align === "end" ? "flex-end" : "center"

const CtaInfoCards = ({
  section,
  sectionTitle,
  flexGrid,
  CTAFlexGrid,
  infoCards,
  button,
  layoutChanger,
  ctaTextAlignment,
  card,
  ctaInfoCardsIconSize,
  iconSize,
  ctaInfoCardsAlign,
  cardsAlign,
  ctaInfoCardsAlignment,
  ctaInfoCardsIconLocation,
  iconLocation,
  filter,
  CTAInfoCFilter,
  filters,
  CTAInfoCFilters,
}) => {
  flexGrid = CTAFlexGrid || flexGrid || { columns: { _: 1 } }
  filter = CTAInfoCFilter || filter
  filters = CTAInfoCFilters || filters || []

  const finalCardTextAlign = ctaInfoCardsAlign || cardsAlign || "left" // info card text alignment
  const textAlignProp = toFlexAlign(ctaTextAlignment ?? "start")
  const infoCardsAlignProp = toFlexAlign(ctaInfoCardsAlignment ?? "center")

  const titleAlign = sectionTitle?.align || "left"
  const ctaAlignItems =
    titleAlign === "right"
      ? "flex-end"
      : titleAlign === "center"
        ? "center"
        : "flex-start"

  const layoutLocation = layoutChanger?.location || "left"
  const isStacked = layoutLocation === "top" || layoutLocation === "bottom"

  return (
    <Section {...section}>
      <LayoutChanger
        itemsMoveStackedOnColumn={true}
        child1Style={{
          alignSelf: toAlignSelf(textAlignProp),
        }}
        child2Style={{
          alignSelf: toAlignSelf(infoCardsAlignProp),
        }}
        {...layoutChanger}
      >
        <Reveal>
          <Flex
            maxWidth={44}
            width="100%"
            flexDirection="column"
            alignItems={ctaAlignItems}
          >
            <SectionTitle {...sectionTitle} mb={6} maxWidth={42} />
            {button && (
              <Flex
                mt={4}
                mb={4}
                flexDirection="column"
                alignItems={ctaAlignItems}
              >
                <Button
                  aria-label="CTA button in info card element"
                  {...button}
                />
              </Flex>
            )}
          </Flex>
        </Reveal>

        <Flex
          width="100%"
          justifyContent={
            isStacked ? toJustifyContent(infoCardsAlignProp) : "flex-start"
          }
        >
          <Box width="100%" maxWidth={isStacked ? 44 : "100%"}>
            <FilteredGrid
              flexGrid={{ flex: "1 1 auto", ...flexGrid }}
              filters={filters}
              filtersStyles={{
                filterLocation: filter?.filterLocation,
                alignMenuItems: filter?.alignMenuItems,
                showFilterLabel: filter?.showFilterLabel,
                layout: filter?.layout,
                filterTextDisplay: filter?.filterTextDisplay,
                dropdownCard: filter?.dropdownCard,
              }} // only sending style fields to avoid possibly overwriting filter data
              card={{
                theme: section.theme,
                bg: section.bg,
                borderRadius: "md",
                // boxShadow: "md",
                boxShadow: "0 3px 10px rgb(0 0 0 / 30%)",
              }}
            >
              {infoCards.map((infoCard, i) => (
                <InfoCard
                  mx={finalCardTextAlign === "center" ? "auto" : 0}
                  iconLocation={ctaInfoCardsIconLocation || iconLocation}
                  iconSize={ctaInfoCardsIconSize || iconSize}
                  align={finalCardTextAlign}
                  {...infoCard}
                  key={i}
                  card={card}
                />
              ))}
            </FilteredGrid>
          </Box>
        </Flex>
      </LayoutChanger>
    </Section>
  )
}

export default CtaInfoCards

CtaInfoCards.propTypes = {
  strapi_id: PropTypes.number.isRequired,
  section: PropTypes.shape(Section.strapiProps).isRequired,
  sectionTitle: PropTypes.shape(SectionTitle.strapiProps).isRequired,
  flexGrid: PropTypes.shape(FlexGrid.strapiProps),
  infoCards: PropTypes.arrayOf(PropTypes.shape(InfoCard.strapiProps)),
  button: PropTypes.shape(Button.strapiProps),
  layoutChanger: PropTypes.shape(LayoutChanger.strapiProps),
  ctaTextAlignment: PropTypes.oneOf(["start", "center", "end"]),
  ctaInfoCardsAlignment: PropTypes.oneOf(["start", "center", "end"]),
  cardsAlign: PropTypes.oneOf(["left", "center", "right"]).isRequired,
  ctaInfoCardsAlign: PropTypes.oneOf(["left", "center", "right"]),
  iconLocation: PropTypes.oneOf(["top", "left"]).isRequired,
  card: PropTypes.shape(Card.strapiProps).isRequired,
}

export const query = graphql`
  fragment CtaInfoCards on STRAPI__COMPONENT_SECTIONS_CTA_INFO_CARDS {
    strapi_id
    CTAINFOCARDSSection: section {
      ...Section
    }
    sectionTitle {
      ...SectionTitle
    }
    layoutChanger {
      ...LayoutChanger
    }
    ctaTextAlignment
    ctaInfoCardsAlignment
    CTAFlexGrid: flexGrid {
      ...FlexGrid
    }
    infoCards {
      ...InfoCard
    }
    button {
      ...Button
    }
    card {
      ...Card
    }
    CTAInfoCFilters: filters {
      ...Filter
    }
    CTAInfoCFilter: filter {
      alignMenuItems
      filterLocation
      showFilterLabel
      layout
      filterTextDisplay
      dropdownCard {
        ...Card
      }
    }
    ctaInfoCardsAlign: cardsAlign
    ctaInfoCardsIconLocation: iconLocation
    ctaInfoCardsIconSize: iconSize
  }
`
